import * as React from 'react';
import {Card, Grid} from '@mui/material';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import {ChartsLabelMark, LineChart, PieChart} from '@mui/x-charts';
import {useMemo} from "react";
import {find, groupBy, map, sumBy, uniq} from "lodash";
import {formatTime} from "@/helper/method";
import dayjs from "dayjs";
import {LineSeriesType} from "@mui/x-charts/models/seriesType/line";
import {XYVO} from '@/service/analyze';
import {lineElementClasses} from "@mui/x-charts/LineChart";


export interface YearContractTrendProps {
    contracts?: XYVO[];
    product?: { [key: string]: XYVO[] }
    loading: boolean
}

type PieDataType = {
    year: string
    datas: any
    total: number
}

const colors = ["#00a1ff", "#00ceb6"]

export default function YearContractTrend({contracts, product, loading}: YearContractTrendProps) {
    const [selectYear, setSelectYear] = React.useState<string>('')

    const {xDatas, lSeries, pDatas, legends} = useMemo(() => {
        const groupYear = groupBy(contracts, item => formatTime("Y", item.x))
        const xDatas = uniq(map(contracts, item => `${dayjs(item.x).format("M")}月`))
        const lSeries: LineSeriesType[] = []
        const legends: { [key: string]: string }[] = []
        Object.keys(groupYear).map((key: string, index: number) => {
            const data = groupYear[key]?.map(item => item.y)
            const label = `${key}年`
            lSeries.push({
                data: data, label, valueFormatter: value => `${value}个`,
                showMark: false, type: 'line', color: colors[index],
            })
            legends.push({label: label, year: key, color: colors[index]})
        })
        const pDatas: PieDataType[] = []
        legends.map((legend, index: number) => {
            const yearDatas: any[] = []
            Object.keys(product!).map((key: string) => {
                const data = product![key]
                const findData = find(data, (item) => parseInt(item.x) == parseInt(legend.year))
                const newData = {
                    label: key,
                    value: findData?.y || 0,
                }
                yearDatas.push(newData)
            })
            if (index + 1 == legends.length) {
                setSelectYear(legend.year)
            }
            pDatas.push({datas: yearDatas, year: legend.year, total: sumBy(yearDatas, item => item.value)})
        })
        return {xDatas, lSeries, pDatas, legends}
    }, [contracts, product]);

    const getPieData = () => {
        const findData = find(pDatas, item => item.year == selectYear)
        return findData?.datas.map((item: any) => ({
            label: item.label,
            value: item.value,
        })) || []
    }

    return (
        <Card variant="outlined" sx={{width: '100%'}}>
            <CardContent>
                <Stack alignContent={'start'} alignItems={'start'} direction={'row'} justifyContent={'space-between'}>
                    <Typography component="h2" variant="subtitle2" gutterBottom>
                        近两年新增合同
                    </Typography>
                    <Stack alignItems={'start'} direction={'row'} spacing={2}>
                        {legends?.map((item, index: number) => <Stack key={index} alignItems={'center'} spacing={1} direction={'row'}>
                            <ChartsLabelMark type={'square'} color={item.color}/>
                            <Typography sx={{display: 'inline-block', alignItems: 'start'}}>{item.label}</Typography>
                        </Stack>)}
                    </Stack>
                </Stack>
                <Grid container={true} spacing={1}>
                    <Grid size={4}>
                        <PieChart
                            margin={{left: 0, right: 0, top: 10, bottom: 0}}
                            // onItemClick={(event, pieItemIdentifier, item) => console.log(item, 'nnnnnnnnnnn')}
                            series={[
                                {
                                    innerRadius: 60,
                                    outerRadius: 80,
                                    id: 'platform-series',
                                    data: pDatas.map(item => ({label: item.year, value: item.total, id: item.year}))
                                },
                                {
                                    innerRadius: 100,
                                    outerRadius: 120,
                                    id: 'OS-series',
                                    data: getPieData(),
                                },
                            ]}
                            width={300}
                            height={180}
                            hideLegend
                        />
                    </Grid>
                    <Grid size={8}>
                        <LineChart
                            height={180}
                            series={lSeries}
                            hideLegend={true}
                            sx={{
                                [`& .${lineElementClasses.root}`]: {
                                    strokeWidth: 4,
                                },
                            }}
                            slotProps={{
                                legend: {
                                    direction: 'horizontal',
                                    position: {
                                        vertical: 'top',
                                        horizontal: 'end',
                                    },
                                    sx: {
                                        gap: '16px',
                                        // CSS class
                                        ['.MuiChartsLegend-series']: {
                                            gap: '8px',
                                        },
                                    },
                                },
                            }}
                            xAxis={[{
                                scaleType: 'band',
                                data: xDatas,
                                tickInterval: (index, i) => i % 2 === 0,
                                disableTicks: true,
                                disableLine: false
                            }]}
                            yAxis={[{width: 28, disableTicks: true, disableLine: true}]}
                            margin={{left: 0, right: 0, top: 10, bottom: 0}}
                        />
                    </Grid>

                </Grid>
            </CardContent>
        </Card>
    );
}
